import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, ArrowLeft, Loader2, Sparkles, Plus, Check, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL;

const GOLD = '#c9a962';
const INK = '#120d0b';
const PANEL = '#1c1512';
const WINE = '#3d1d24';

const Monogram = () => (
  <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ border: `1px solid ${GOLD}66`, backgroundColor: `${GOLD}14` }}>
    <KeyRound className="w-7 h-7" style={{ color: GOLD }} strokeWidth={1.2} />
  </div>
);

const Wordmark = ({ sub }) => (
  <div className="text-center">
    <p className="text-[10px] tracking-[0.4em] uppercase mt-6" style={{ color: `${GOLD}99` }}>Minted Presents</p>
    <h1 className="text-2xl sm:text-3xl mt-2 tracking-[0.12em]" style={{ fontFamily: 'Georgia, serif', color: '#f0e9df' }}>
      The Private Collection
    </h1>
    <div className="mx-auto mt-3 w-16 h-px" style={{ backgroundColor: `${GOLD}66` }} />
    {sub && <p className="text-sm mt-4 leading-relaxed max-w-xs mx-auto" style={{ color: '#a89a8c' }}>{sub}</p>}
  </div>
);

const GoldButton = ({ children, onClick, disabled, testid, ghost }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className="w-full py-3.5 rounded-sm text-sm tracking-[0.15em] uppercase transition-all disabled:opacity-50 hover:opacity-90"
    style={ghost
      ? { border: `1px solid ${GOLD}55`, color: GOLD, backgroundColor: 'transparent' }
      : { backgroundColor: GOLD, color: INK, fontWeight: 600 }}
  >
    {children}
  </button>
);

export default function PrivateCollectionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useApp();
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState('');
  const [dob, setDob] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showSellSheet, setShowSellSheet] = useState(false);
  const [entered, setEntered] = useState(false);

  const loadStatus = useCallback(async () => {
    const res = await fetch(`${API}/api/private-collection/status${user?.id ? `?user_id=${user.id}` : ''}`);
    const data = await res.json();
    setStatus(data);
    return data;
  }, [user?.id]);

  const loadProducts = useCallback(async (cat = '') => {
    if (!user?.id) return;
    try {
      const res = await fetch(`${API}/api/private-collection/products?user_id=${user.id}${cat ? `&category=${encodeURIComponent(cat)}` : ''}`);
      if (res.ok) setProducts(await res.json());
    } catch {}
  }, [user?.id]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { if (status?.member) loadProducts(category); }, [status?.member, category, loadProducts]);
  useEffect(() => { const t = setTimeout(() => setEntered(true), 150); return () => clearTimeout(t); }, []);

  // Full-viewport after-dark backdrop while inside the Collection
  useEffect(() => {
    document.body.classList.add('pc-dark');
    return () => document.body.classList.remove('pc-dark');
  }, []);

  // Self-healing Stripe return verification
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const pcType = searchParams.get('pc');
    if (!sessionId || !pcType) return;
    window.history.replaceState({}, '', '/private-collection');
    setVerifying(true);
    const verify = async (attempt = 0) => {
      try {
        const res = await fetch(`${API}/api/private-collection/verify?session_id=${sessionId}`);
        const data = await res.json();
        if (data.success) {
          toast.success(pcType === 'membership' ? 'Welcome to The Private Collection' :
            pcType === 'seller_pass' ? 'Seller Pass activated — list freely' : 'Your piece is now in the Collection');
          await loadStatus();
          setVerifying(false);
          return;
        }
      } catch {}
      if (attempt < 4) setTimeout(() => verify(attempt + 1), 3500);
      else { setVerifying(false); toast.error("We couldn't confirm the payment yet — it may take a minute."); }
    };
    verify();
  }, [searchParams, loadStatus]);

  const verifyAge = async () => {
    if (!dob) { toast.error('Please enter your date of birth'); return; }
    if (!confirmed) { toast.error('Please confirm you are over 18'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/users/${user.id}/verify-age`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date_of_birth: dob, confirmed_over_18: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Verification failed');
      await loadStatus();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const checkoutPost = async (path) => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 20000);
    try {
      const res = await fetch(`${API}${path}`, { method: 'POST', signal: ctrl.signal });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Something went wrong');
      return data;
    } catch (e) {
      if (e.name === 'AbortError') throw new Error("That took too long — nothing was charged. Please try again.");
      throw e;
    } finally {
      clearTimeout(timer);
    }
  };

  const join = async () => {
    setBusy(true);
    try {
      const data = await checkoutPost(`/api/private-collection/join?user_id=${user.id}`);
      if (data.checkout_url) window.location.href = data.checkout_url;
      else if (data.already_member) { loadStatus(); setBusy(false); }
    } catch (e) { toast.error(e.message); setBusy(false); }
  };

  const buySellerPass = async () => {
    setBusy(true);
    try {
      const data = await checkoutPost(`/api/private-collection/seller-pass?user_id=${user.id}`);
      if (data.checkout_url) window.location.href = data.checkout_url;
      else if (data.already_owned) { toast.success('You already hold a Seller Pass'); loadStatus(); setBusy(false); }
    } catch (e) { toast.error(e.message); setBusy(false); }
  };

  const Shell = ({ children, center }) => (
    <div className="min-h-screen" style={{ backgroundColor: INK }}>
      <meta name="robots" content="noindex, nofollow" />
      <div className={`max-w-lg mx-auto px-6 pb-16 transition-all duration-700 ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'} ${center ? 'flex flex-col justify-center min-h-screen py-10' : 'pt-6'}`}>
        <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-xs tracking-wider uppercase mb-6" style={{ color: '#7a6c5f' }} data-testid="pc-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Minted
        </button>
        {children}
      </div>
    </div>
  );

  if (!status || verifying) {
    return (
      <Shell center>
        <div className="text-center">
          <Monogram />
          <Wordmark sub={verifying ? 'Confirming with our concierge…' : null} />
          <Loader2 className="w-5 h-5 animate-spin mx-auto mt-8" style={{ color: GOLD }} />
        </div>
      </Shell>
    );
  }

  if (!isAuthenticated) {
    return (
      <Shell center>
        <Monogram />
        <Wordmark sub="A discreet boutique for grown-up tastes. Entry is by membership, for verified adults only." />
        <div className="mt-10 space-y-3">
          <GoldButton testid="pc-signin-btn" onClick={() => navigate('/login?redirect=/private-collection')}>Sign in to request entry</GoldButton>
        </div>
      </Shell>
    );
  }

  if (!status.age_verified) {
    return (
      <Shell center>
        <Monogram />
        <Wordmark sub="Before the velvet rope lifts, we need to know you're of age. Verified once, remembered always." />
        <div className="mt-10 rounded-sm p-6 space-y-5" style={{ backgroundColor: PANEL, border: `1px solid ${GOLD}26` }} data-testid="pc-age-gate">
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase mb-2" style={{ color: `${GOLD}99` }}>Date of birth</label>
            <input type="date" value={dob} onChange={e => setDob(e.target.value)} data-testid="pc-dob-input"
              className="w-full px-3 py-2.5 rounded-sm text-sm outline-none"
              style={{ backgroundColor: INK, border: `1px solid ${GOLD}33`, color: '#f0e9df', colorScheme: 'dark' }} />
          </div>
          <label className="flex items-start gap-3 cursor-pointer text-sm" style={{ color: '#a89a8c' }}>
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} data-testid="pc-confirm-checkbox"
              className="mt-0.5 accent-[#c9a962]" />
            I confirm I am 18 years or older
          </label>
          <GoldButton testid="pc-verify-age-btn" onClick={verifyAge} disabled={busy}>
            {busy ? 'One moment…' : 'Verify my age'}
          </GoldButton>
        </div>
      </Shell>
    );
  }

  if (!status.member) {
    return (
      <Shell center>
        <Monogram />
        <Wordmark sub="Beyond this point lies a members-only boutique — intimate pieces, adult wellness, lingerie, media and private services. Shown only to members, never to the marketplace." />
        <div className="mt-10 rounded-sm p-6 text-center" style={{ backgroundColor: PANEL, border: `1px solid ${GOLD}26` }} data-testid="pc-membership-card">
          <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: `${GOLD}99` }}>Membership</p>
          <p className="mt-3" style={{ fontFamily: 'Georgia, serif', color: '#f0e9df' }}>
            <span className="text-4xl">${(status.prices?.membership_monthly || 4.99).toFixed(2)}</span>
            <span className="text-sm" style={{ color: '#a89a8c' }}> / month</span>
          </p>
          <ul className="mt-4 space-y-2 text-left text-sm max-w-[260px] mx-auto" style={{ color: '#a89a8c' }}>
            {['Full access to the Collection', 'Discreet — nothing appears on the public marketplace', 'Cancel anytime from Stripe receipts'].map(l => (
              <li key={l} className="flex gap-2"><Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: GOLD }} />{l}</li>
            ))}
          </ul>
          <div className="mt-6">
            <GoldButton testid="pc-join-btn" onClick={join} disabled={busy}>
              {busy ? 'Opening checkout…' : 'Request entry'}
            </GoldButton>
          </div>
          <p className="text-[11px] mt-3" style={{ color: '#7a6c5f' }}>Billed securely by Stripe · 18+ only</p>
        </div>
      </Shell>
    );
  }

  // ---- Inside the Collection ----
  const cats = status.categories || [];
  return (
    <Shell>
      <div className="text-center" data-testid="pc-store">
        <Monogram />
        <Wordmark sub={null} />
        <p className="text-[11px] tracking-[0.2em] uppercase mt-3" style={{ color: '#7a6c5f' }}>
          <Sparkles className="w-3 h-3 inline mr-1" style={{ color: GOLD }} />
          {status.lifetime ? 'Lifetime member' : 'Member'} · welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
        </p>
        {status.cancelled && status.member_until && (
          <p className="text-[11px] mt-2" style={{ color: '#a89a8c' }} data-testid="pc-cancelled-note">
            Membership cancelled — access until {new Date(status.member_until).toLocaleDateString()}
          </p>
        )}
        {!status.lifetime && !status.cancelled && (
          <button
            onClick={async () => {
              if (!window.confirm('Cancel your membership? Billing stops immediately and you keep access for 30 more days.')) return;
              try {
                const res = await fetch(`${API}/api/private-collection/cancel?user_id=${user.id}`, { method: 'POST' });
                const d = await res.json();
                if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Something went wrong');
                toast.success(`Cancelled — access until ${new Date(d.access_until).toLocaleDateString()}`);
                loadStatus();
              } catch (e) { toast.error(e.message); }
            }}
            className="text-[10px] tracking-wider uppercase underline mt-2"
            style={{ color: '#5c5047' }}
            data-testid="pc-cancel-membership-btn"
          >
            Cancel membership
          </button>
        )}
      </div>

      <div className="flex gap-2 overflow-x-auto mt-8 pb-2 scrollbar-hide">
        {['', ...cats].map(c => (
          <button key={c || 'all'} onClick={() => setCategory(c)} data-testid={`pc-cat-${(c || 'all').toLowerCase().replace(/[^a-z]+/g, '-')}`}
            className="px-4 py-1.5 rounded-full text-xs whitespace-nowrap tracking-wider transition-colors"
            style={category === c
              ? { backgroundColor: GOLD, color: INK, fontWeight: 600 }
              : { border: `1px solid ${GOLD}33`, color: '#a89a8c' }}>
            {c || 'All'}
          </button>
        ))}
      </div>

      <button onClick={() => setShowSellSheet(true)} data-testid="pc-list-item-btn"
        className="w-full mt-4 rounded-sm p-4 flex items-center justify-between group transition-colors"
        style={{ backgroundColor: WINE, border: `1px solid ${GOLD}33` }}>
        <span className="text-sm" style={{ color: '#f0e9df' }}>
          <Plus className="w-4 h-4 inline mr-2" style={{ color: GOLD }} />Offer a piece to the Collection
        </span>
        <span className="text-[10px] tracking-wider uppercase" style={{ color: `${GOLD}99` }}>Sellers</span>
      </button>

      {products.length === 0 ? (
        <div className="text-center mt-14" data-testid="pc-empty">
          <p style={{ fontFamily: 'Georgia, serif', color: '#a89a8c' }}>The cabinet is being curated.</p>
          <p className="text-xs mt-2" style={{ color: '#7a6c5f' }}>Be the first to offer a piece.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 mt-6">
          {products.map(p => (
            <button key={p.id} onClick={() => navigate(`/product/${p.id}`)} data-testid={`pc-product-${p.id}`}
              className="text-left rounded-sm overflow-hidden transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: PANEL, border: `1px solid ${GOLD}1f` }}>
              <div className="aspect-[4/5] overflow-hidden" style={{ backgroundColor: '#241b16' }}>
                {p.image ? <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                  : <div className="w-full h-full flex items-center justify-center"><KeyRound className="w-6 h-6" style={{ color: `${GOLD}44` }} /></div>}
              </div>
              <div className="p-3">
                <p className="text-sm line-clamp-1" style={{ fontFamily: 'Georgia, serif', color: '#f0e9df' }}>{p.title}</p>
                <p className="text-sm mt-1 font-semibold" style={{ color: GOLD }}>${Number(p.price).toFixed(2)}</p>
                {p.location && <p className="text-[10px] mt-1 flex items-center gap-1" style={{ color: '#7a6c5f' }}><MapPin className="w-3 h-3" />{p.location}</p>}
              </div>
            </button>
          ))}
        </div>
      )}

      {showSellSheet && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }} onClick={() => setShowSellSheet(false)}>
          <div className="w-full max-w-md rounded-t-xl sm:rounded-sm p-6" style={{ backgroundColor: PANEL, border: `1px solid ${GOLD}33` }} onClick={e => e.stopPropagation()} data-testid="pc-sell-sheet">
            <h3 className="text-lg text-center" style={{ fontFamily: 'Georgia, serif', color: '#f0e9df' }}>Offer a piece</h3>
            <p className="text-xs text-center mt-1 mb-5" style={{ color: '#a89a8c' }}>Listings in the Collection are placed with care — and a small consideration.</p>
            <div className="space-y-3">
              <button onClick={() => { setShowSellSheet(false); navigate('/sell?collection=private'); }} data-testid="pc-sell-single-btn"
                className="w-full rounded-sm p-4 text-left" style={{ border: `1px solid ${GOLD}44` }}>
                <p className="text-sm" style={{ color: '#f0e9df' }}>Single placement</p>
                <p className="text-xs mt-0.5" style={{ color: '#a89a8c' }}>${(status.prices?.listing_fee || 4.99).toFixed(2)} per piece, paid after you create the listing</p>
              </button>
              <button onClick={status.seller_pass ? () => { setShowSellSheet(false); navigate('/sell?collection=private'); } : buySellerPass} disabled={busy} data-testid="pc-seller-pass-btn"
                className="w-full rounded-sm p-4 text-left relative" style={{ backgroundColor: `${GOLD}14`, border: `1px solid ${GOLD}66` }}>
                <p className="text-sm" style={{ color: GOLD }}>{status.seller_pass ? 'Seller Pass — active ✓' : 'Seller Pass'}</p>
                <p className="text-xs mt-0.5" style={{ color: '#a89a8c' }}>
                  {status.seller_pass ? 'Unlimited placements — start listing' : `One-time $${(status.prices?.seller_pass || 14.99).toFixed(2)} · unlimited placements, forever`}
                </p>
              </button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
